import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type RouteRecordRaw,
} from 'vue-router';

import {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  localePrefix,
  type Locale,
} from '@/i18n/locales';
import { PAGES, RETIRED_SLUGS, routeName, slugFor } from './pages';

declare module 'vue-router' {
  interface RouteMeta {
    locale: Locale;
    pageKey: string;
  }
}

/**
 * Joins a locale prefix and a slug into a leading-slash path.
 *
 * The slug is percent-encoded because vue-router matches `location.pathname`
 * literally, without decoding it. Several slugs are non-ASCII — Estonian
 * `privaatsuspoliitika-ja-küpsised` and all the Russian ones — and a browser
 * always reports those encoded, so a route registered with raw Unicode would
 * never match a real navigation and the page would 404.
 */
function buildPath(locale: Locale, slug: string): string {
  const prefix = localePrefix(locale);
  if (!slug) {
    return prefix || '/';
  }
  return `${prefix}/${encodeURIComponent(slug)}`;
}

function buildRoutes(): RouteRecordRaw[] {
  const routes: RouteRecordRaw[] = [];

  for (const locale of SUPPORTED_LOCALES) {
    for (const page of PAGES) {
      routes.push({
        path: buildPath(locale, slugFor(page, locale)),
        name: routeName(page.key, locale),
        component: page.component,
        meta: { locale, pageKey: page.key },
      });
    }

    // Retired slugs redirect to the locale's home page.
    for (const slugs of RETIRED_SLUGS) {
      const slug = slugs[locale] ?? slugs[DEFAULT_LOCALE];
      routes.push({
        path: buildPath(locale, slug),
        redirect: { name: routeName('home', locale) },
      });
    }
  }

  // Unknown paths fall through to the Estonian home page, matching the
  // catch-all behaviour of the old server-side redirects.
  routes.push({
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { locale: DEFAULT_LOCALE, pageKey: 'not-found' },
  });

  return routes;
}

export const router = createRouter({
  history: createWebHistory(),
  routes: buildRoutes(),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    if (to.hash) {
      // Accordion targets are expanded by the page, so the element may not
      // exist on the first frame — vue-router retries via `el`.
      return { el: to.hash, behavior: 'smooth', top: 120 };
    }
    if (to.path === from.path) {
      return false;
    }
    return { top: 0 };
  },
});

/** Reads the active locale off a resolved route. */
export function localeOf(route: RouteLocationNormalized): Locale {
  return route.meta.locale ?? DEFAULT_LOCALE;
}

export default router;
